
import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, XCircle, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface StoreBannerUploaderProps {
  value: string | File | null | undefined; // Can be current URL (string), new File, or null/undefined
  onChange: (fileOrUrl: File | string | null) => void; // To update RHF state
  // userId prop removed as it's not directly used for upload logic here, ProfileSettings will handle upload.
}

const StoreBannerUploader: React.FC<StoreBannerUploaderProps> = ({ value, onChange }) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isInitialUrl, setIsInitialUrl] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    if (typeof value === 'string' && value) {
      setPreview(value);
      setFileName(null);
      setIsInitialUrl(true);
    } else if (value instanceof File) {
      objectUrl = URL.createObjectURL(value);
      setPreview(objectUrl);
      setFileName(value.name);
      setIsInitialUrl(false);
    } else {
      setPreview(null);
      setFileName(null);
      setIsInitialUrl(false);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [value]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      onChange(file); // Pass the File object to RHF
    }
  }, [onChange]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false,
  });

  const handleRemoveImage = () => {
    onChange(null); // Signal removal (clear field in RHF)
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Baneri i Dyqanit</label>
      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-Primary-Purple transition-colors',
          isDragActive ? 'border-Primary-Purple bg-Primary-Purple/10' : 'border-gray-300 dark:border-gray-600',
          fileRejections.length > 0 ? 'border-red-500 dark:border-red-700' : ''
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center space-y-2">
          <UploadCloud className="w-12 h-12 text-gray-400 dark:text-gray-500" />
          {isDragActive ? (
            <p className="text-Primary-Purple">Lësho imazhin këtu...</p>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">Zvarrit & lësho imazhin, ose kliko për të zgjedhur</p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500">PNG, JPG, GIF, WEBP deri në 5MB. Rekomandohet: ~1200-1600px gjerësi.</p>
        </div>
      </div>

      {fileRejections.length > 0 && (
        <div className="text-red-500 dark:text-red-400 text-sm">
          {fileRejections[0].errors[0].message === 'File is larger than 5242880 bytes'
            ? 'Skedari është më i madh se 5MB.'
            : fileRejections[0].errors[0].message}
        </div>
      )}

      {preview && (
        <div className="mt-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg relative group bg-gray-50 dark:bg-Charcoal-Gray/30">
          <p className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">
            {fileName ? `Parapamje e "${fileName}"` : "Baneri Aktual"}
          </p>
          <div className="aspect-[16/5] md:aspect-[16/4] rounded-md overflow-hidden bg-gray-200 dark:bg-gray-600">
            <img src={preview} alt="Banner Preview" className="w-full h-full object-cover" />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 h-8 w-8 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-50 group-hover:opacity-100 transition-opacity"
            onClick={handleRemoveImage}
            title="Hiqe Imazhin"
          >
            <XCircle size={20} />
          </Button>
        </div>
      )}
      {!preview && (
         <div className="mt-4 p-6 border border-gray-200 dark:border-gray-700 rounded-lg text-center bg-gray-50 dark:bg-Charcoal-Gray/30">
            <ImageIcon size={48} className="mx-auto text-gray-300 dark:text-gray-500" />
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">Nuk ka baner të zgjedhur</p>
         </div>
      )}
    </div>
  );
};

export default StoreBannerUploader;
